/**
 * Fallback images when a listing has no seller photos.
 * Uses stable upload.wikimedia.org thumb URLs (not wiki Special:FilePath — breaks in browsers under load).
 * @see https://commons.wikimedia.org/
 */

/** Direct CDN thumbs — verified via Commons API (iiurlwidth=330) */
const COMMONS_DIRECT: Record<string, string> = {
  'Samsung_Galaxy_S23_Series.jpg':
    'https://upload.wikimedia.org/wikipedia/commons/thumb/6/6c/Samsung_Galaxy_S23_Series.jpg/330px-Samsung_Galaxy_S23_Series.jpg',
  'Back_of_the_Samsung_Galaxy_S23.jpg':
    'https://upload.wikimedia.org/wikipedia/commons/thumb/a/a8/Back_of_the_Samsung_Galaxy_S23.jpg/330px-Back_of_the_Samsung_Galaxy_S23.jpg',
  'Front_of_the_Samsung_Galaxy_Z_Fold_4.jpg':
    'https://upload.wikimedia.org/wikipedia/commons/thumb/b/b5/Front_of_the_Samsung_Galaxy_Z_Fold_4.jpg/330px-Front_of_the_Samsung_Galaxy_Z_Fold_4.jpg',
  'Foldable_smartphones.jpg':
    'https://upload.wikimedia.org/wikipedia/commons/thumb/6/66/Foldable_smartphones.jpg/330px-Foldable_smartphones.jpg',
  'Google_Pixel_8_Rose_front.jpg':
    'https://upload.wikimedia.org/wikipedia/commons/thumb/5/5c/Google_Pixel_8_Rose_front.jpg/330px-Google_Pixel_8_Rose_front.jpg',
  'Redmi Note 12 front.jpg':
    'https://upload.wikimedia.org/wikipedia/commons/thumb/b/be/Redmi_Note_12_front.jpg/330px-Redmi_Note_12_front.jpg',
  'Xiaomi 13 front.jpg':
    'https://upload.wikimedia.org/wikipedia/commons/thumb/0/04/Xiaomi_13_front.jpg/330px-Xiaomi_13_front.jpg',
  'Smartphone_Nokia_basato_su_Android.jpg':
    'https://upload.wikimedia.org/wikipedia/commons/thumb/0/04/Smartphone_Nokia_basato_su_Android.jpg/330px-Smartphone_Nokia_basato_su_Android.jpg',
  'Motorola_Edge_30_Ultra_front.jpg':
    'https://upload.wikimedia.org/wikipedia/commons/thumb/5/53/Motorola_Edge_30_Ultra_front.jpg/330px-Motorola_Edge_30_Ultra_front.jpg',
  'Motorola edge 40 neo.jpg':
    'https://upload.wikimedia.org/wikipedia/commons/thumb/7/75/Motorola_edge_40_neo.jpg/330px-Motorola_edge_40_neo.jpg',
  'Hand_holding_Smartphone.jpg':
    'https://upload.wikimedia.org/wikipedia/commons/thumb/0/0b/Hand_holding_Smartphone.jpg/330px-Hand_holding_Smartphone.jpg',
  'Nothing_Phone_(1)_back_-_white.jpg':
    'https://upload.wikimedia.org/wikipedia/commons/thumb/3/3b/Nothing_Phone_%281%29_back_-_white.jpg/330px-Nothing_Phone_%281%29_back_-_white.jpg',
  'IPhone_13_vector.svg': 'https://upload.wikimedia.org/wikipedia/commons/0/0c/IPhone_13_vector.svg',
  'IPhone_14_Pro_vector.svg':
    'https://upload.wikimedia.org/wikipedia/commons/thumb/9/9f/IPhone_14_Pro_vector.svg/330px-IPhone_14_Pro_vector.svg.png',
  'IPhone_16_Pro_Max_Vector.svg':
    'https://upload.wikimedia.org/wikipedia/commons/thumb/f/fc/IPhone_16_Pro_Max_Vector.svg/330px-IPhone_16_Pro_Max_Vector.svg.png',
}

const COMMONS_REDIRECT =
  'https://commons.wikimedia.org/w/index.php?title=Special:Redirect/file/'

const VECTORS = {
  iphone13: 'IPhone_13_vector.svg',
  iphone14Pro: 'IPhone_14_Pro_vector.svg',
  iphone16ProMax: 'IPhone_16_Pro_Max_Vector.svg',
} as const

const PHOTO = {
  samsungLineup: 'Samsung_Galaxy_S23_Series.jpg',
  samsungBack: 'Back_of_the_Samsung_Galaxy_S23.jpg',
  samsungFoldOpen: 'Front_of_the_Samsung_Galaxy_Z_Fold_4.jpg',
  samsungFoldablesPair: 'Foldable_smartphones.jpg',
  googlePixel8Front: 'Google_Pixel_8_Rose_front.jpg',
  redmiNote12Front: 'Redmi Note 12 front.jpg',
  xiaomi13Front: 'Xiaomi 13 front.jpg',
  nokiaAndroid: 'Smartphone_Nokia_basato_su_Android.jpg',
  motorolaEdgeFront: 'Motorola_Edge_30_Ultra_front.jpg',
  motorolaEdge40Neo: 'Motorola edge 40 neo.jpg',
  handSmartphone: 'Hand_holding_Smartphone.jpg',
} as const

function commonsUrl(filename: string, width: number): string {
  const direct = COMMONS_DIRECT[filename]
  if (direct) return direct
  return `${COMMONS_REDIRECT}${encodeURIComponent(filename)}&width=${width}`
}

function norm(s: string): string {
  return s.toLowerCase().replace(/\s+/g, ' ').trim()
}

function stableHash(s: string): number {
  let h = 0
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0
  return h
}

function pickSamsungPhoto(model: string, width: number): string {
  const x = norm(model)
  if (x.includes('fold') && !x.includes('flip')) {
    return commonsUrl(PHOTO.samsungFoldOpen, width)
  }
  if (x.includes('flip')) {
    return commonsUrl(PHOTO.samsungFoldablesPair, width)
  }
  const pool = [PHOTO.samsungLineup, PHOTO.samsungBack] as const
  const idx = stableHash(model) % pool.length
  return commonsUrl(pool[idx], width)
}

/**
 * Returns a stable image URL for the given manufacturer + model.
 */
export function getStockPhoneImageUrl(
  manufacturer: string,
  modelName: string,
  width: number = 400,
): string {
  const m = norm(manufacturer)
  const model = norm(modelName)
  const combined = `${m} ${model}`

  if (m.includes('apple') || combined.includes('iphone')) {
    const v = model.match(/iphone\s*(?:se\s*)?(\d{1,2})/i) || model.match(/\b(\d{1,2})\b/)
    const gen = v ? parseInt(v[1], 10) : 14
    if (gen >= 16) return commonsUrl(VECTORS.iphone16ProMax, width)
    if (gen === 15) return commonsUrl(VECTORS.iphone16ProMax, width)
    if (gen === 14) return commonsUrl(VECTORS.iphone14Pro, width)
    return commonsUrl(VECTORS.iphone13, width)
  }

  if (m.includes('samsung') || combined.includes('galaxy')) {
    return pickSamsungPhoto(modelName, width)
  }

  if (m.includes('google') || combined.includes('pixel')) {
    return commonsUrl(PHOTO.googlePixel8Front, width)
  }

  if (m.includes('xiaomi') || m.includes('poco') || m.includes('redmi')) {
    const x = norm(modelName)
    const isRedmiNote =
      (x.includes('redmi') && x.includes('note')) || (m.includes('redmi') && x.includes('note'))
    if (isRedmiNote) {
      return commonsUrl(PHOTO.redmiNote12Front, width)
    }
    return commonsUrl(PHOTO.xiaomi13Front, width)
  }

  if (m.includes('nokia')) {
    return commonsUrl(PHOTO.nokiaAndroid, width)
  }

  if (m.includes('motorola') || m.includes('moto')) {
    const x = norm(modelName)
    if (x.includes('edge') && x.includes('40')) {
      return commonsUrl(PHOTO.motorolaEdge40Neo, width)
    }
    return commonsUrl(PHOTO.motorolaEdgeFront, width)
  }

  if (m.includes('nothing')) {
    return commonsUrl('Nothing_Phone_(1)_back_-_white.jpg', width)
  }

  if (combined.includes('fold') || combined.includes('flip')) {
    return commonsUrl(PHOTO.samsungFoldablesPair, width)
  }

  return commonsUrl(PHOTO.handSmartphone, width)
}

export function hasListingPhotos(images: { length?: number } | null | undefined): boolean {
  const n = images?.length
  return typeof n === 'number' && n > 0
}
